/**
 * Provenance of web-gathered drafts (S4 T10): the source page link and the verbatim quote
 * behind a draft row or a pending identity field. Read-only, cache-friendly TanStack query,
 * same degrade-to-empty convention as useBbExtractions: the endpoint failing must never
 * surface as error noise on mount, the drafts just show without a source.
 */
import { useQuery } from '@tanstack/react-query';
import { bbSourcesKeys } from './bbSourcesKeys';
import { brandBibleV2Service, fromApiTable } from '@/services/brandBibleV2Service';

/** One source of a draft. `url` is null for a Google Business Profile value (never invented). */
export interface SourceInfo {
  kind: 'web' | 'gbp' | 'questionnaire';
  url: string | null;
  /** The server's source_ref ("Questionnaire sheet · <tab> · row N") for a questionnaire answer. */
  ref?: string | null;
  quote: string;
  matched_in?: string;
  fetched_via?: string;
}

export { bbSourcesKeys };

/** `services:<id>` for a list row, `identity:<id>:<field>` for an identity field. */
export function sourceKey(table: string, entryId: string, field?: string): string {
  const short = fromApiTable(table);
  return field ? `${short}:${entryId}:${field}` : `${short}:${entryId}`;
}

export function useBbSources(tenantId: string | null) {
  return useQuery({
    queryKey: bbSourcesKeys.list(tenantId ?? ''),
    queryFn: async (): Promise<Map<string, SourceInfo[]>> => {
      const map = new Map<string, SourceInfo[]>();
      if (!tenantId) return map;
      try {
        const rows = await brandBibleV2Service.getSources(tenantId);
        const ordered = [...rows].sort((a, b) => String(a.at).localeCompare(String(b.at)));
        for (const row of ordered) {
          const key = sourceKey(row.table_name, row.entry_id, row.field_name || undefined);
          const info: SourceInfo = {
            kind: row.source_kind === 'gbp_api' ? 'gbp' : row.source_kind === 'questionnaire' ? 'questionnaire' : 'web',
            url: row.source_url,
            ...(row.source_kind === 'questionnaire' ? { ref: row.source_ref ?? null } : {}),
            quote: row.quote,
            matched_in: row.matched_in,
            fetched_via: row.fetched_via,
          };
          map.set(key, [...(map.get(key) ?? []), info]);
        }
      } catch {
        // Provenance is a nice-to-have, not core data: degrade to an empty map.
        return new Map();
      }
      return map;
    },
    enabled: !!tenantId,
    staleTime: 60_000,
  });
}
